import { getRequirementsTitleText } from "../";
import { useI18n, type GettextInterface } from "../../../../../../../../../shared/I18n";
import { useTareData } from "../../../../../../../../../shared/native-modules/tare/TareDataContext";
import Sentiment from "../../../../../../../../../shared/util/salary/Sentiment";
import { useModalContext } from "../../../../context/JMUIModalContextProvider";
import { useFeatureFlags } from "../../../../context/useFeatureFlags";
import { useLogging } from "../../../../context/useLogging";
import JobTagStrictness from "../../../../requirements/JobTagStrictness";
import type { QualificationAttribute } from "../../../../types/JobAttribute";
import { getTileVariant, type TileProps } from "../../../common/Tile";
import { getInlineQuestion } from "../useQualificationSection";
import { getQualificationResponse } from "../useQualificationSections";

type GetTilePropsParams = {
  attribute: QualificationAttribute;
  answeredIds: string[];
  onModalSubmit: (p: {
    attribute: QualificationAttribute;
    sentiment: Sentiment;
  }) => Promise<boolean>;
  onModalReset: (attribute: QualificationAttribute) => Promise<boolean>;
};

function getQualificationIconAriaLabel(
  gt: GettextInterface,
  isMatch: boolean,
  isNegativeMatch: boolean,
) {
  if (isMatch) {
    return gt.gettext("Yes, has qualification");
  }
  if (isNegativeMatch) {
    return gt.gettext("No, does not have qualification");
  }
  return undefined;
}

export function useQualificationTiles({
  attribute,
  onModalSubmit,
  onModalReset,
}: GetTilePropsParams): TileProps {
  const { openCollectionModal } = useModalContext();
  const tareData = useTareData();
  const loggedIn = tareData.accountStatus?.isLoggedIn ?? false;
  const { shouldShowTypicallyRequired, shouldShowCrowtaxoFrontend } = useFeatureFlags();
  const logger = useLogging();
  const gt = useI18n();
  const isNegativeMatch: boolean = attribute.sentiment === Sentiment.NEGATIVE || false; // to prevent undefined warnings
  const isMatch = attribute.sentiment === Sentiment.POSITIVE;
  const isRequired = attribute.strictness === JobTagStrictness.REQUIRED;
  const isTypicallyRequired =
    !!shouldShowTypicallyRequired && attribute.strictness === JobTagStrictness.TYPICALLY_REQUIRED;

  const iconAriaLabel = getQualificationIconAriaLabel(gt, isMatch, isNegativeMatch);

  return {
    variant: getTileVariant({
      isMatch,
      isNegativeMatch,
    }),
    primaryText: attribute.label,
    isRequired,
    isTypicallyRequired,
    iconAriaLabel: iconAriaLabel,
    onPress: loggedIn
      ? () => {
          const originalSentiment = attribute.sentiment;

          openCollectionModal(
            {
              title: getRequirementsTitleText(attribute.type, gt),
              question: getInlineQuestion(attribute, gt),
              editDisclaimer: shouldShowCrowtaxoFrontend
                ? gt.pgettext(
                    "Modal qualification edit disclaimer",
                    "Editing or clearing your response may remove skills associated with [{0}|{1}] from your [profile|{2}].",
                    [attribute.label, "highlight", "link"],
                  )
                : undefined,
              inferenceMethod: attribute.inferenceMethod,
              onModalReset: () => onModalReset(attribute),
              onModalSubmit: async ({ sentiment }) => {
                const originalInferenceMethod = attribute.inferenceMethod;
                const result = await onModalSubmit({
                  attribute,
                  sentiment,
                });

                const qualificationResponse = getQualificationResponse(sentiment);
                logger.logQualificationsQuestionButtonClick(
                  qualificationResponse,
                  attribute,
                  result,
                  originalInferenceMethod!,
                );
                return result;
              },
              data: {
                sentiment: originalSentiment,
              },
              onClose: () => {
                logger.logEditableQualificationsModalClose(attribute);
              },
              showSalary: false,
            },
            loggedIn,
          );
        }
      : undefined,
  };
}
